import type { Lap } from "./laps";
import type { LapLabel } from "@contract/types";
import { matchLabelToLapIdx } from "./strokeOverrides";

/** How the fix was made, as a whole sentence. Absent provenance = a legacy record
 *  from before we tracked this; those are all his own picks, so say so. */
function howText(p: LapLabel["provenance"]): string {
  if (p === "suggestion-accepted") return "You took the app's suggestion.";
  if (p === "user-confirmed") return "You said the watch was right.";
  return "You picked this yourself."; // "user-corrected", null, or absent
}

export interface CorrectionRecord {
  lapIndex: number;      // swim-wide lap index — identity only, NEVER displayed
  setIndex: number;      // 1-based set number
  setLabel: string;      // e.g. "Set 4"
  lapPosInSet: number;   // 0-based position within its set — DISPLAY as lapPosInSet + 1
  from: string;          // what the watch recorded
  to: string;            // what he changed it to
  howText: string;       // plain words
  atMs: number | null;   // null on legacy records — never invent a time
  excluded: boolean;     // true when this fixed length ALSO doesn't count in totals
}

export interface CorrectionsViewModel {
  changed: CorrectionRecord[]; // the only thing that counts or gets marked
  changedLapIndices: Set<number>; // O(1) lookup for the ribbon + set-detail marks
  confirmedCount: number;
  unmatchedCount: number; // saved fixes we could not line up with a length — disclosed, not hidden
}

/** The one headline for the corrections card. "Fixed" (not "set ... straight")
 *  because this screen already uses "set" four other times as swim vocabulary
 *  (Warmup, Mixed set, Cooldown) — a blind panel read "5 lengths straight" as
 *  five CONSECUTIVE lengths, not five fixes. Singular for exactly one. */
export function correctionsHeadline(n: number): string {
  return n === 1 ? "You fixed 1 length" : `You fixed ${n} lengths`;
}

/** A fresh empty view model.
 *
 *  Deliberately a factory, not a shared const. Every open-water swim and every swim
 *  with no labels gets one of these, so a single shared object would be handed out
 *  repeatedly — and one stray `.add()` on its Set (or `.push()` on its array) would
 *  then leak corrections from one swim into every other. `Object.freeze` would NOT
 *  prevent that: freezing an object does not stop `Set.add` or `Array.push` on its
 *  contents. Handing back a fresh object is what actually makes it safe.
 */
export function emptyCorrections(): CorrectionsViewModel {
  return {
    changed: [],
    changedLapIndices: new Set(),
    confirmedCount: 0,
    unmatchedCount: 0,
  };
}

/** Minimal shape we need from a set group (deriveWorkout's SetGroup satisfies this). */
interface SetLike {
  index: number;
  label: string;
  laps: Lap[];
}

export function buildCorrectionsViewModel(
  laps: Lap[],
  setGroups: SetLike[],
  labels: LapLabel[],
): CorrectionsViewModel {
  if (labels.length === 0) return emptyCorrections();

  const lapKeys = laps.map((l) => l.correctionKey);

  // Where does each lap sit for DISPLAY? Set number + position within that set.
  // We show the in-set position because that is what SetDetailSheet shows; the
  // swim-wide index would send him looking for a "Length 43" he will never see.
  const place = new Map<number, { setIndex: number; setLabel: string; pos: number }>();
  for (const set of setGroups) {
    set.laps.forEach((l, pos) => {
      place.set(l.index, { setIndex: set.index, setLabel: set.label, pos });
    });
  }

  const changed: CorrectionRecord[] = [];
  const changedLapIndices = new Set<number>();
  let confirmedCount = 0;
  let unmatchedCount = 0;

  for (const label of labels) {
    // Only stroke decisions are in scope. A pure keep/exclude or a plain drill name
    // is not a stroke fix and has no place here.
    const isStrokeDecision =
      label.strokeOverride != null || label.provenance === "user-confirmed";
    if (!isStrokeDecision) continue;

    const idx = matchLabelToLapIdx(label, laps, lapKeys);
    if (idx === null) {
      unmatchedCount++; // a saved fix we cannot line up. Disclosed in the UI, never dropped.
      continue;
    }

    if (label.provenance === "user-confirmed") {
      confirmedCount++;
      continue;
    }

    const lap = laps[idx];
    // The one inclusion test: did anything actually change from what the watch said?
    // This is what stops a corrected-BACK-to-watch record counting as a fix.
    if (lap.strokeStyle === lap.watchStrokeStyle) continue;

    const at = place.get(lap.index);
    changed.push({
      lapIndex: lap.index,
      setIndex: at?.setIndex ?? 0,
      setLabel: at?.setLabel ?? "",
      lapPosInSet: at?.pos ?? 0,
      from: lap.watchStrokeStyle,
      to: lap.strokeStyle,
      howText: howText(label.provenance),
      atMs: label.createdAtMs ?? null,
      excluded: lap.effectiveExcluded,
    });
    changedLapIndices.add(lap.index);
  }

  return { changed, changedLapIndices, confirmedCount, unmatchedCount };
}
